/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
/* eslint-disable @typescript-eslint/dot-notation */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SizeProp } from "@fortawesome/fontawesome-svg-core";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  OnChangeIndex,
  OnHideCols,
  OnResetCols,
  TableSortKey,
} from "../../types/components/datatable";
import { capitalizeFirstLetter, getKeyFromCol } from "../../utils/utils";
import React, { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import { DropDirection } from "react-bootstrap/esm/DropdownContext";
import { Column } from "react-table";
import { ColDragAndDrop } from "./ColDragAndDrop";
import { ConfirmModal } from "../../components/Modal/ConfirmModal";

interface ITableFilterModal {
  isShowResult?: boolean;
  drop: DropDirection;
  columns: Column[] | unknown[];
  colsHide?: Array<string>;
  onHideCols?: OnHideCols;
  onResetCols?: OnResetCols;
  isDark?: boolean;
  onChangeIndex?: OnChangeIndex;
  sortKeys: TableSortKey[];
  resetAndHideSortedColumn?: (key: string) => void;
}
/**
 * Component show/hide columns of table.
 * @param  ITableFilterModal
 * @returns React Element
 */
export const TableFilterModal: FC<ITableFilterModal> = ({
  isShowResult,
  drop = "down",
  columns,
  colsHide = [],
  onHideCols,
  isDark,
  onChangeIndex,
  onResetCols,
  sortKeys = [],
  resetAndHideSortedColumn,
}) => {
  const { t } = useTranslation();
  const [currentColumn, setCurrentColum] = useState<Column | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [showConfirmHideSortedColumn, setShowConfirmHideSortedColumn] =
    useState(false);
  /**
   * Function
   */
  // @ts-ignore
  const handleCheck = (e, column: Column) => {
    e.preventDefault();

    if (!onHideCols) return;

    const colKey = getKeyFromCol(column);
    if (colKey === "") return;

    const isCurrentlyVisible = !colsHide.includes(colKey);
    const isSorted = sortKeys.some((s) => s.id === colKey);

    // Hiding a visible column
    if (isCurrentlyVisible) {
      if (isSorted) {
        setShowConfirmHideSortedColumn(true);
        setCurrentColum(column);
      } else {
        onHideCols([...colsHide, colKey]);
      }
      return;
    }

    // Showing a hidden column — no confirm needed
    onHideCols(colsHide.filter((c) => c !== colKey));
  };

  /**
   * Class and style of Component
   */
  const dropdownStyle = useMemo(() => {
    return {
      position: "absolute",
      right: !isShowResult ? 10 : 20,
      top: !isShowResult ? -40 : 27,
      zIndex: 1000,
      background: "transparent",
      border: 0,
      transform: "translateZ(0)",
    } as CSSProperties;
  }, [isShowResult]);

  const dropdownClassName = useMemo(() => {
    return `custom-dropdown ${isDark ? "dark text-white" : ""}`;
  }, [isDark]);

  const toggleClassName = useMemo(() => {
    return `${isDark ? "text-white" : ""} ${
      !isShowResult ? "filter-no-result" : "border-none p-lr-none"
    }  `;
  }, [isDark, isShowResult]);

  // Cap the menu height; header and footer stay pinned while only the column list scrolls.
  const menuContentStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    maxHeight: "70vh",
  };
  const fixedSectionStyle: CSSProperties = { flexShrink: 0 };
  const scrollableListStyle: CSSProperties = {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
  };

  const toggleId = `dropdown-split-basic${new Date().getTime()}`;

  useEffect(() => {
    if (columns)
      setFiltered(
        // @ts-ignore
        columns.filter((cl) => cl && cl?.id !== "action" && cl.show)
      );
  }, [columns]);

  const handleDrop = (droppedId: string, index: number) => {
    const columnToMove = filtered.find(
      // @ts-ignore
      (x) => x.id === droppedId
    );
    const newTs: Column[] | unknown[] = filtered.filter(
      // @ts-ignore
      (c) => c.id !== droppedId
    );

    if (!index) return;
    newTs.splice(index, 0, columnToMove);
    const reindexColumns = newTs.map((i, idx) => {
      return {
        // @ts-ignore
        ...i,
        index: idx,
      };
    });
    // @ts-ignore
    setFiltered(reindexColumns);
    if (onChangeIndex) onChangeIndex(reindexColumns);
  };
  // @ts-ignore
  const getColumnLabel = (column) => {
    const label = column["Header"] || capitalizeFirstLetter(column["id"]);
    // Specific label
    const final = label === "StorageClass" ? "Storage Class" : label;
    return final;
  };

  const handleConfirmHideSortedColumns = () => {
    if (!currentColumn || !onHideCols) {
      setShowConfirmHideSortedColumn(false);
      return;
    }
    const colKey = getKeyFromCol(currentColumn);
    if (colKey === "") {
      setShowConfirmHideSortedColumn(false);
      return;
    }
    // hide column
    onHideCols([...colsHide, colKey]);
    // hide modal
    setCurrentColum(null);
    setShowConfirmHideSortedColumn(false);
  };

  const handleCancelModalSortedColumns = () => {
    if (!currentColumn || !onHideCols) {
      setShowConfirmHideSortedColumn(false);
      return;
    }
    const colKey = getKeyFromCol(currentColumn);
    if (colKey === "") {
      setShowConfirmHideSortedColumn(false);
      return;
    }
    // hide modal and reset sorted
    setShowConfirmHideSortedColumn(false);
    // dispatching to change sort
    resetAndHideSortedColumn?.(getKeyFromCol(currentColumn));
    setCurrentColum(null);
  };

  return (
    <>
      {showConfirmHideSortedColumn && (
        <ConfirmModal
          show={showConfirmHideSortedColumn}
          cancel={handleCancelModalSortedColumns}
          confirm={handleConfirmHideSortedColumns}
          body={t("modal:confirm_hide_sorted_message")}
          onClose={() => {}}
        />
      )}

      <Dropdown className={dropdownClassName} drop={drop} style={dropdownStyle}>
        <Dropdown.Toggle
          variant={isShowResult ? "primary" : "light"}
          id={toggleId}
          className={toggleClassName}
        >
          <i className="fa fa-cog" onClick={() => setShowMenu(true)}></i>
        </Dropdown.Toggle>
        <Dropdown.Menu show={showMenu}>
          <div style={menuContentStyle}>
            <div
              className="p-lr-sm border-bottom p-b-xs"
              style={fixedSectionStyle}
            >
              <div className="d-flex align-items-center">
                <h6>{t("table:columns")} </h6>
                <span className="m-l-xxs">
                  (
                  {t("table:hidden_cls", {
                    count: colsHide.length,
                  })}
                  )
                </span>
              </div>
            </div>
            <div style={scrollableListStyle}>
              {filtered.map((column, index) => {
                return (
                  <ColDragAndDrop
                    // @ts-ignore
                    dataItem={column?.id}
                    handleDroppedCol={(droppedId: string) => {
                      handleDrop(droppedId, index);
                    }}
                    key={index}
                    className="d-flex align-items-center p-tb-xs p-lr-sm cursor-pointer border-bottom dragging-item"
                  >
                    <>
                      <div className="d-flex justify-content-between w-100 align-items-center">
                        <span
                          className="d-flex justify-content-between"
                          onClick={(e) => {
                            handleCheck(e, column);
                          }}
                        >
                          <input
                            className="cursor-pointer"
                            type="checkbox"
                            checked={!colsHide.includes(getKeyFromCol(column))}
                            readOnly
                          />
                          <span className="p-l-xs">
                            {getColumnLabel(column)}
                          </span>
                        </span>
                        <FontAwesomeIcon
                          className="cursor-grab drag-icon"
                          color={isDark ? "#FFFFFF" : "#6b6b6b"}
                          size={"lg" as SizeProp}
                          icon={["fas", "grip-vertical"]}
                        />
                      </div>
                    </>
                  </ColDragAndDrop>
                );
              })}
            </div>
            <div
              className="d-flex align-items-center p-t-xs p-lr-sm  justify-content-between"
              style={fixedSectionStyle}
            >
              <OverlayTrigger
                placement="left"
                trigger={["hover", "focus"]}
                overlay={
                  <Tooltip id="overlay-example-emails">
                    <div>{t("table:drag_info")}</div>
                  </Tooltip>
                }
              >
                <i
                  aria-hidden
                  className="fa fa-question-circle cursor-pointer"
                />
              </OverlayTrigger>
              <button
                type="button"
                onClick={() => {
                  // Rest hide columns.
                  onResetCols?.();
                }}
                className="btn btn-primary text-white"
              >
                Reset
              </button>
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};
